import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import MobileStickyBar from '../components/common/MobileStickyBar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  CalendarDays, MapPin, Users, Plus, ArrowRight,
  PartyPopper, Heart, Briefcase, Cake, Star,
  Trash2, Edit3, Share2, ExternalLink
} from 'lucide-react';
import { formatDate } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = [
  { id: 'anniversaire_enfant', label: 'Anniversaire enfant', icon: '🎂', color: '#F4A7B2', bg: 'var(--rose-mist)' },
  { id: 'anniversaire_adulte', label: 'Anniversaire adulte', icon: '🥂', color: '#D4AF37', bg: 'rgba(212,175,55,0.08)' },
  { id: 'mariage', label: 'Mariage', icon: '💍', color: '#5DBCBF', bg: 'var(--teal-mist)' },
  { id: 'entreprise', label: 'Entreprise / CE', icon: '💼', color: '#7DB1FF', bg: 'rgba(125,177,255,0.08)' },
  { id: 'soiree', label: 'Soirée', icon: '🎉', color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)' },
  { id: 'bapteme', label: 'Baptême & communion', icon: '🕊️', color: '#5DBCBF', bg: 'var(--teal-mist)' },
  { id: 'garden_party', label: 'Garden party', icon: '🌿', color: '#1F9D7A', bg: 'rgba(31,157,122,0.08)' },
  { id: 'autre', label: 'Autre', icon: '✨', color: '#CFCFCF', bg: 'rgba(207,207,207,0.08)' },
];

const getType = (id) => EVENT_TYPES.find(t => t.id === id) || EVENT_TYPES[EVENT_TYPES.length - 1];

function EventCard({ event, onDelete }) {
  const type = getType(event.event_type);
  const dateLabel = event.date_evenement
    ? formatDate(event.date_evenement)
    : 'Date à définir';
  const daysLeft = event.date_evenement
    ? Math.ceil((new Date(event.date_evenement) - Date.now()) / 86400000)
    : null;

  return (
    <div data-testid={`event-card-${event.event_id}`}
      className="card-premium overflow-hidden group"
      style={{ border: `1px solid ${type.color}20` }}
    >
      {/* Bande colorée top */}
      <div style={{ height: 4, background: `linear-gradient(90deg, ${type.color} 0%, ${type.color}60 100%)` }} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{type.icon}</span>
            <div>
              <h3 className="font-display font-bold text-base leading-tight" style={{ color: 'var(--ink)' }}>
                {event.title}
              </h3>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full inline-block mt-0.5"
                style={{ background: type.bg, color: type.color }}>
                {type.label}
              </span>
            </div>
          </div>
          {daysLeft !== null && daysLeft >= 0 && (
            <span className="text-xs font-bold px-2.5 py-1.5 rounded-xl flex-shrink-0"
              style={{
                background: daysLeft <= 7 ? 'rgba(244,167,178,0.15)' : 'var(--teal-mist)',
                color: daysLeft <= 7 ? '#D94B5D' : '#0B4B4E',
              }}>
              J-{daysLeft}
            </span>
          )}
        </div>

        {/* Infos */}
        <div className="space-y-1.5 mb-4">
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
            <CalendarDays size={13} style={{ color: type.color }} />
            {dateLabel}
          </div>
          {event.lieu && (
            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
              <MapPin size={13} style={{ color: type.color }} />
              {event.lieu}
            </div>
          )}
          {event.nb_guests > 0 && (
            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
              <Users size={13} style={{ color: type.color }} />
              {event.nb_guests} invité(s)
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Link
            to={`/mon-evenement/${event.slug}`}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all"
            style={{ background: type.bg, color: type.color, border: `1px solid ${type.color}25` }}
          >
            Gérer <ArrowRight size={12} />
          </Link>
          <button
            onClick={() => {
              navigator.clipboard?.writeText(`${window.location.origin}/mon-evenement/${event.slug}`);
              toast.success('Lien copié !');
            }}
            className="p-2 rounded-xl transition-colors"
            style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}
            title="Copier le lien de partage"
          >
            <Share2 size={14} />
          </button>
          <button
            onClick={() => onDelete(event.event_id)}
            className="p-2 rounded-xl transition-colors"
            style={{ background: 'rgba(217,75,93,0.06)', color: '#D94B5D' }}
            title="Archiver l'événement"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateEventModal({ onClose, onCreated, userInfo }) {
  const [form, setForm] = useState({
    title: '', event_type: 'anniversaire_enfant',
    date_evenement: '', lieu: '', description: '', nombre_invites: '',
  });
  const [loading, setLoading] = useState(false);

  async function handleCreate() {
    if (!form.title.trim()) { toast.error('Donnez un nom à votre événement'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          nombre_invites: form.nombre_invites ? parseInt(form.nombre_invites) : null,
          owner_user_id: userInfo?.id,
          owner_name: userInfo?.nom,
          owner_email: userInfo?.email,
        }),
      });
      const created = await res.json();
      toast.success('Événement créé ! 🎉');
      onCreated(created);
    } catch { toast.error('Erreur lors de la création'); }
    setLoading(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ background: 'rgba(26,26,46,0.5)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-lg rounded-3xl overflow-hidden" style={{ background: '#FEFAF5', boxShadow: '0 32px 80px rgba(26,26,46,0.2)' }}>
        {/* Header modal */}
        <div className="flex items-center justify-between p-6 pb-4">
          <div>
            <h2 className="font-display text-xl font-bold" style={{ color: 'var(--ink)' }}>Créer un événement</h2>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Votre espace de gestion personnalisé</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl" style={{ background: 'var(--bg)', color: 'var(--text-muted)' }}>
            ✕
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {/* Nom */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--ink)' }}>Nom de l'événement *</label>
            <input
              data-testid="create-event-title"
              type="text"
              placeholder="Ex : Anniversaire Lucas, Mariage Julie & Thomas…"
              value={form.title}
              onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl text-sm"
              style={{ background: '#FFFFFF', border: '1.5px solid var(--border)', color: 'var(--ink)' }}
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--ink)' }}>Type d'événement</label>
            <div className="grid grid-cols-4 gap-2">
              {EVENT_TYPES.map(t => (
                <button key={t.id} onClick={() => setForm(p => ({ ...p, event_type: t.id }))}
                  className="flex flex-col items-center gap-1 p-2 rounded-xl text-xs font-medium transition-all"
                  style={{
                    background: form.event_type === t.id ? t.bg : '#FFFFFF',
                    border: `1.5px solid ${form.event_type === t.id ? t.color : 'var(--border)'}`,
                    color: form.event_type === t.id ? t.color : 'var(--text-muted)',
                  }}
                >
                  <span className="text-base">{t.icon}</span>
                  <span className="leading-tight text-center" style={{ fontSize: 9 }}>{t.label.split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Date + Lieu */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--ink)' }}>Date</label>
              <input type="date" value={form.date_evenement}
                onChange={e => setForm(p => ({ ...p, date_evenement: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm"
                style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--ink)' }}>Invités (nb)</label>
              <input type="number" min="1" max="9999" placeholder="50"
                value={form.nombre_invites}
                onChange={e => setForm(p => ({ ...p, nombre_invites: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm"
                style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }}
              />
            </div>
          </div>

          {/* Lieu */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--ink)' }}>Lieu</label>
            <input type="text" placeholder="Salle des fêtes, Maison…"
              value={form.lieu}
              onChange={e => setForm(p => ({ ...p, lieu: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-xl text-sm"
              style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }}
            />
          </div>

          {/* CTA */}
          <button
            data-testid="create-event-submit"
            onClick={handleCreate}
            disabled={loading || !form.title.trim()}
            className="w-full btn-teal py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40"
          >
            {loading ? 'Création…' : <><Plus size={16} /> Créer mon événement</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MesEvenementsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    fetchEvents();
  // eslint-disable-next-line
  }, [user]);

  async function fetchEvents() {
    setLoading(true);
    try {
      const params = user?.id ? `?owner_user_id=${user.id}` : '';
      const r = await fetch(`${BACKEND}/api/events${params}`);
      const d = await r.json();
      setEvents((d.events || []).filter(e => e.statut !== 'archive'));
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setLoading(false);
  }

  async function handleDelete(event_id) {
    if (!window.confirm('Archiver cet événement ?')) return;
    await fetch(`${BACKEND}/api/events/${event_id}`, { method: 'DELETE' });
    toast.success('Événement archivé');
    setEvents(prev => prev.filter(e => e.event_id !== event_id));
  }

  function handleCreated(event) {
    setEvents(prev => [event, ...prev]);
    setShowCreate(false);
    navigate(`/mon-evenement/${event.slug}`);
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      <div className="container-ose" style={{ paddingTop: '2.5rem', paddingBottom: '5rem' }}>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold mb-1"
              style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              Mes événements
            </h1>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {user ? `Bonjour ${user.nom || user.email} — ` : ''}Gérez tous vos événements depuis un seul endroit.
            </p>
          </div>
          <button
            data-testid="create-event-btn"
            onClick={() => setShowCreate(true)}
            className="btn-teal px-5 py-3 rounded-xl text-sm font-bold flex items-center gap-2 flex-shrink-0"
          >
            <Plus size={16} /> Nouvel événement
          </button>
        </div>

        {/* Contenu */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={`col-${i}`} className="h-52 rounded-2xl animate-pulse" style={{ background: 'rgba(26,26,46,0.06)' }} />
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20">
            <PartyPopper size={56} className="mx-auto mb-5" style={{ color: 'var(--border)' }} />
            <h2 className="font-display text-xl font-bold mb-3" style={{ color: 'var(--ink)' }}>
              Prêt à créer votre premier événement ?
            </h2>
            <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: 'var(--text-muted)', lineHeight: 1.7 }}>
              Organisez, invitez, suivez — tout depuis un espace dédié.
              Chaque événement devient son propre mini-site partageable.
            </p>
            <button
              onClick={() => setShowCreate(true)}
              className="btn-teal px-8 py-3.5 rounded-xl text-sm font-bold inline-flex items-center gap-2"
            >
              <Plus size={16} /> Créer mon premier événement
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {events.map(ev => (
              <EventCard key={ev.event_id} event={ev} onDelete={handleDelete} />
            ))}
          </div>
        )}

        {/* Lien configurateur */}
        {events.length > 0 && (
          <div className="mt-10 p-5 rounded-2xl flex items-center justify-between gap-4"
            style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
            <div>
              <p className="font-semibold text-sm" style={{ color: '#0B4B4E' }}>Vous cherchez du matériel pour l'un de vos événements ?</p>
              <p className="text-xs" style={{ color: 'rgba(11,75,78,0.7)' }}>Utilisez notre configurateur pour un devis complet</p>
            </div>
            <Link to="/creer-evenement" className="btn-teal px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap flex items-center gap-1.5">
              Configurateur <ArrowRight size={13} />
            </Link>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateEventModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
          userInfo={user}
        />
      )}

      <MobileStickyBar />
      <Footer />
    </div>
  );
}
